/**
 * Login screen — email/password sign-in plus Sign in with Apple.
 *
 * PORTED from apps/cryb-native/app/(auth)/login.tsx (P3 AUTH group):
 *   - expo-router useRouter → react-navigation useNavigation. router.push(...)
 *     for sibling auth screens; on a successful login we do NOT navigate — the
 *     useAuth() gate swaps the root tree (login persists the token → onAuthChange
 *     → gate re-resolves to 'authed'/'onboarding'). This replaces the old
 *     router.replace('/(tabs)/home').
 *   - expo-apple-authentication + expo-crypto → services/apple-auth (invertase).
 *     The lib generates+SHA256-hashes the nonce and returns the RAW nonce, which
 *     we forward to the backend as `rawNonce` (byte-identical anti-replay).
 *   - @expo/vector-icons Ionicons → components/ui/Icon.
 *
 * P1-AUTH REDESIGN: the old lavender LinearGradient + static blob decorations +
 * plain <Text>CRYB</Text> are replaced by the design system — ParticleBackground
 * (living animated wash), a GlassSurface card, and LogoWordmark. All surfaces,
 * spacing, radius, type and color reference tokens — no hardcoded rgba/hex/px.
 *
 * Auth calls go through services/api (login / appleSignIn — both persist the
 * session token in the api layer). New Apple users still route through onboarding
 * via setOnboardingPending(true) + the gate.
 */
import { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  KeyboardAvoidingView,
  ScrollView,
  Platform,
  Alert,
  Pressable,
  useWindowDimensions,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Ionicons } from '../components/ui/Icon';
import { isAppleAuthAvailable, isAppleAuthAvailableSync, performAppleLogin } from '../services/apple-auth';
import AppleSignInButton from '../components/AppleSignInButton';
import ParticleBackground from '../components/ui/ParticleBackground';
import LogoWordmark from '../components/LogoWordmark';
import { GlassSurface, GlassButton } from '../components/glass';
import { login, appleSignIn, setOnboardingPending } from '../services/api';
import { notifyAuthGate } from '../navigation/useAuth';
import type { AuthStackParamList } from '../navigation/types';
import { colors, spacing, radius, fonts, typographyScale, getLayout } from '../constants/theme';

type Nav = NativeStackNavigationProp<AuthStackParamList, 'Login'>;

export default function LoginScreen() {
  const navigation = useNavigation<Nav>();
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const layout = getLayout(width);
  const cardWidth = layout !== 'phone'
    ? { maxWidth: layout === 'desktop' ? 460 : 420, alignSelf: 'center' as const, width: '100%' as const }
    : undefined;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  // SYNC availability check as the lazy initial state — the invertase
  // `isSupported` flag is a plain boolean, so the Apple button is decided BEFORE
  // the first paint (no async mount probe → no pop-in on interaction, and
  // keyboard events can never affect it). Availability is still re-verified
  // asynchronously at tap time inside handleAppleSignIn.
  const [appleAvailable] = useState(() => isAppleAuthAvailableSync());

  // ── Handlers ──
  // Email/password sign-in. On success the api layer persists the token and the
  // useAuth() gate swaps the root tree to the app — no manual navigation here.
  async function handleLogin() {
    if (!email.trim() || !password.trim()) {
      Alert.alert('Missing fields', 'Please enter your email and password.');
      return;
    }

    setLoading(true);
    const result = await login(email.trim(), password);
    setLoading(false);

    if (result.success) {
      // Returning user → straight to home. (Email login never creates an account,
      // so there's no onboarding race here; clear the flag defensively in case a
      // prior aborted signup left it set on this module instance.) The gate routes.
      setOnboardingPending(false);
      notifyAuthGate();
    } else {
      const msg = result.error || '';
      const lower = msg.toLowerCase();
      const title =
        /lock/.test(lower) ? 'Account locked'
        : /too many|rate|429/.test(lower) ? 'Too many attempts'
        : 'Login failed';
      Alert.alert(title, msg || 'Invalid email or password. Please try again.');
    }
  }

  // Sign in with Apple. Exchanges Apple's identity token for a CRYB session via
  // appleSignIn(); user cancellation is swallowed silently (not an error).
  async function handleAppleSignIn() {
    try {
      // Re-verify availability right before invoking — guards against a device
      // where the capability isn't actually provisioned (prevents native crash).
      const available = await isAppleAuthAvailable().catch(() => false);
      if (!available) {
        Alert.alert('Unavailable', 'Sign in with Apple is not available on this device.');
        return;
      }
      // SECURITY: invertase generates a random raw nonce → SHA-256 hash sent to
      // Apple (embedded in the identityToken `nonce` claim) and returns the RAW
      // nonce; the backend re-hashes and verifies the match to defeat token replay.
      const credential = await performAppleLogin();
      if (!credential) return; // user canceled the sheet

      setLoading(true);
      const result = await appleSignIn(
        credential.identityToken,
        credential.fullName,
        credential.email,
        credential.rawNonce
      );
      setLoading(false);

      if (result.success) {
        // First-time Apple users must complete signup/onboarding even when they
        // tapped "Sign in" — the backend tells us via isNewUser. Set the pending
        // flag so the gate routes to onboarding (not home).
        if ((result as any).data?.isNewUser) {
          setOnboardingPending(true);
        } else {
          setOnboardingPending(false);
        }
        notifyAuthGate();
      } else {
        Alert.alert('Sign in failed', result.error || 'Could not sign in with Apple.');
      }
    } catch (e: any) {
      setLoading(false);
      // performAppleLogin already swallows the cancel code (returns null); any
      // throw here is a real failure (entitlement/provisioning).
      Alert.alert('Sign in failed', 'Could not complete Apple Sign In. Please try again.');
    }
  }

  return (
    <View style={styles.root}>
      <ParticleBackground theme="light" count={7} />
      <KeyboardAvoidingView
        style={styles.flex}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <ScrollView
          style={styles.flex}
          contentContainerStyle={[
            styles.scrollContent,
            { paddingTop: insets.top + spacing.xxl, paddingBottom: insets.bottom + spacing.xxl },
          ]}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          <View style={[styles.stack, cardWidth]}>
            {/* Brand — centered bare wordmark (no glass bubble, per feedback). */}
            <View style={styles.brand}>
              <LogoWordmark size={46} style={styles.logoCentered} />
              <Text style={styles.tagline}>Welcome back</Text>
            </View>

            <GlassSurface variant="card" radius={radius.xl} contentStyle={styles.cardBody} style={styles.card}>
              {/* Sign in with Apple (custom HIG button) */}
              {appleAvailable && (
                <>
                  <View style={styles.appleAuthButton}>
                    <AppleSignInButton
                      label="Sign in with Apple"
                      onPress={handleAppleSignIn}
                      disabled={loading}
                    />
                  </View>
                  <View style={styles.dividerRow}>
                    <View style={styles.dividerLine} />
                    <Text style={styles.dividerText}>or</Text>
                    <View style={styles.dividerLine} />
                  </View>
                </>
              )}

              {/* Email */}
              <TextInput
                style={styles.input}
                placeholder="Email"
                placeholderTextColor={colors.muted}
                value={email}
                onChangeText={setEmail}
                autoCapitalize="none"
                keyboardType="email-address"
                textContentType="username"
                autoComplete="email"
                returnKeyType="next"
              />

              {/* Password with eye toggle */}
              <View style={styles.passwordWrap}>
                <TextInput
                  style={styles.passwordInput}
                  placeholder="Password"
                  placeholderTextColor={colors.muted}
                  value={password}
                  onChangeText={setPassword}
                  secureTextEntry={!showPass}
                  textContentType="password"
                  autoComplete="password"
                  returnKeyType="done"
                  onSubmitEditing={handleLogin}
                />
                <Pressable
                  onPress={() => setShowPass(p => !p)}
                  style={styles.eyeBtn}
                  hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
                  accessibilityRole="button"
                  accessibilityLabel={showPass ? 'Hide password' : 'Show password'}
                >
                  <Ionicons name={showPass ? 'eye-off-outline' : 'eye-outline'} size={20} color={colors.muted} />
                </Pressable>
              </View>

              {/* Forgot password */}
              <TouchableOpacity
                style={styles.forgotRow}
                activeOpacity={0.6}
                onPress={() => navigation.navigate('ForgotPassword')}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                accessibilityRole="button"
                accessibilityLabel="Forgot password"
              >
                <Text style={styles.forgotText}>Forgot password?</Text>
              </TouchableOpacity>

              {/* Sign In button */}
              <GlassButton
                title="Sign In"
                onPress={handleLogin}
                loading={loading}
                disabled={loading}
                fullWidth
                size="lg"
              />
            </GlassSurface>

            {/* Register link */}
            <TouchableOpacity
              style={styles.bottomRow}
              onPress={() => navigation.navigate('Register')}
              activeOpacity={0.6}
              accessibilityRole="button"
              accessibilityLabel="Create a new account"
            >
              <Text style={styles.bottomText}>
                No account?{' '}
                <Text style={styles.bottomLink}>Create one free</Text>
              </Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.bg },
  flex: { flex: 1 },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingHorizontal: spacing.xxl,
  },
  stack: {
    width: '100%',
  },
  brand: {
    alignItems: 'center',
    marginBottom: spacing.xxl,
  },
  logoGlass: {
    alignSelf: 'center',
  },
  logoGlassInner: {
    paddingHorizontal: spacing.xl,
    paddingVertical: spacing.md,
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoCentered: {
    alignSelf: 'center',
  },
  tagline: {
    ...typographyScale.body,
    color: colors.ink2,
    marginTop: spacing.sm,
  },
  card: {
    marginBottom: spacing.xl,
  },
  cardBody: {
    padding: spacing.xxl,
  },
  passwordWrap: {
    position: 'relative',
    marginBottom: spacing.md,
  },
  passwordInput: {
    height: 48,
    backgroundColor: colors.white,
    borderRadius: radius.md,
    paddingHorizontal: spacing.lg,
    paddingRight: 44,
    ...typographyScale.body,
    color: colors.ink,
    borderWidth: 1,
    borderColor: colors.cardBorder,
  },
  eyeBtn: {
    position: 'absolute',
    right: spacing.md,
    top: 14,
  },
  appleAuthButton: {
    height: 48,
    width: '100%',
    marginBottom: spacing.md,
  },
  dividerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: colors.cardBorder,
  },
  dividerText: {
    marginHorizontal: spacing.md,
    ...typographyScale.caption,
    color: colors.muted,
  },
  input: {
    height: 48,
    backgroundColor: colors.white,
    borderRadius: radius.md,
    paddingHorizontal: spacing.lg,
    ...typographyScale.body,
    color: colors.ink,
    borderWidth: 1,
    borderColor: colors.cardBorder,
    marginBottom: spacing.md,
  },
  forgotRow: {
    alignSelf: 'flex-end',
    marginBottom: spacing.lg,
  },
  forgotText: {
    ...typographyScale.caption,
    color: colors.primary,
    ...fonts.semibold,
  },
  bottomRow: {
    alignSelf: 'center',
  },
  bottomText: {
    ...typographyScale.body,
    color: colors.ink2,
  },
  bottomLink: {
    color: colors.primary,
    ...fonts.semibold,
  },
});
